/**
 * 订单详情弹窗（全视角通用）
 * 任何页面传入 orderId 即可弹出订单完整详情（支付/合同/分账/物流/发票）。
 */

import { Card, Badge, useApi, money, time, Loading, Button } from './ui'
import { api } from '../api/client'

interface Detail {
  order: {
    order_id: string
    platform_order_id: string
    source_platform: string
    partnership_id: string
    amount_total: string
    amount_refunded: string
    confirm_time: string | null
    logistics_no: string | null
    synced_at: string
    status: string
    buyer_ref: unknown
  }
  contract: { contract_id: string; commission_rate: string; signed_at: string; core_clauses: unknown } | null
  settlements: Array<{ settlement_id: string; status: string; seller_amount: string; factory_amount_net: string; platform_fee: string; bank_serial_no: string; executed_at: string | null }>
  invoices: Array<{ invoice_id: string; invoice_category: string; issuer_id: string; issuer_role: string; amount: string; tax_rate: string; remark: string; item_name: string; status: string; invoice_no: string; pdf_url: string | null; issued_at: string | null }>
  logistics: Array<{ logistics_id: string; carrier: string; tracking_no: string; shipper_id: string; status: string; uploaded_at: string }>
}

export function OrderDetailModal({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const detail = useApi<Detail>(async () => api.get(`/orders/${orderId}`), [orderId])

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 24, overflow: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#f9fafb', borderRadius: 8, maxWidth: 900, width: '100%', padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>订单 {orderId}</h2>
          <Button size="sm" variant="default" onClick={onClose}>关闭</Button>
        </div>

        {detail.loading ? <Loading /> : detail.error || !detail.data ? <Loading error={detail.error || '无数据'} /> : (
          <>
            <div style={{ marginBottom: 8 }}><Badge status={detail.data.order.status} /></div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <Card title="💳 支付详情">
                <div style={{ fontSize: 13 }}>
                  <Field label="支付状态" value={detail.data.order.status === 'PAID' ? '已支付' : detail.data.order.status === 'REFUNDED' ? '已退款' : detail.data.order.status} />
                  <Field label="订单金额" value={money(detail.data.order.amount_total)} />
                  <Field label="支付方式" value="电商平台在线支付" />
                  <Field label="电商原单号" value={detail.data.order.platform_order_id} />
                  <Field label="来源平台" value={detail.data.order.source_platform} />
                  <Field label="同步时间" value={time(detail.data.order.synced_at)} />
                  {Number(detail.data.order.amount_refunded) > 0 && <div style={{ marginTop: 8, color: '#991b1b' }}>已退款：{money(detail.data.order.amount_refunded)}</div>}
                </div>
              </Card>

              <Card title="📋 合同详情">
                {detail.data.contract ? (
                  <div style={{ fontSize: 13 }}>
                    <Field label="合同ID" value={detail.data.contract.contract_id} />
                    <Field label="佣金率" value={`${(parseFloat(detail.data.contract.commission_rate) * 100).toFixed(1)}%`} />
                    <Field label="签署时间" value={detail.data.contract.signed_at ? time(detail.data.contract.signed_at) : '-'} />
                  </div>
                ) : <div style={{ color: '#888', fontSize: 13 }}>无合同</div>}
              </Card>

              <Card title="💰 分账详情">
                {detail.data.settlements.length > 0 ? (
                  <div style={{ fontSize: 13 }}>
                    {detail.data.settlements.map((s) => (
                      <div key={s.settlement_id} style={{ marginBottom: 8 }}>
                        <div><Badge status={s.status} /> · 流水 {s.bank_serial_no}</div>
                        <div>商家服务费：{money(s.seller_amount)}</div>
                        <div>工厂货款：{money(s.factory_amount_net)}</div>
                        <div>平台抽佣：{money(s.platform_fee)}</div>
                      </div>
                    ))}
                  </div>
                ) : <div style={{ color: '#888', fontSize: 13 }}>未分账</div>}
              </Card>

              <Card title="📦 物流详情">
                {detail.data.logistics.length > 0 ? (
                  <div style={{ fontSize: 13 }}>
                    {detail.data.logistics.map((l) => (
                      <div key={l.logistics_id}>
                        <Field label="物流公司" value={l.carrier} />
                        <Field label="运单号" value={l.tracking_no} />
                        <Field label="寄件人" value={`${l.shipper_id}（工厂直发）`} />
                        <Field label="状态" value={l.status} />
                      </div>
                    ))}
                  </div>
                ) : <div style={{ color: '#888', fontSize: 13 }}>无物流</div>}
              </Card>
            </div>

            <Card title="🧾 发票详情（数电票）">
              {detail.data.invoices.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                  {detail.data.invoices.map((inv) => (
                    <div key={inv.invoice_id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                      {inv.pdf_url && inv.status === 'ISSUED' ? (
                        <img src={inv.pdf_url} alt={inv.invoice_no ?? ''} style={{ width: '100%', display: 'block' }} />
                      ) : (
                        <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', color: '#888', fontSize: 12 }}>{inv.status}</div>
                      )}
                      <div style={{ padding: 8, fontSize: 12 }}>
                        <div style={{ fontWeight: 600 }}>{inv.invoice_category === 'GOODS' ? '商品票(13%)' : '服务费票(6%)'} <Badge status={inv.status} /></div>
                        <div style={{ color: '#666' }}>{inv.invoice_no} · {money(inv.amount)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ color: '#888', fontSize: 13 }}>未开票</div>}
            </Card>
          </>
        )}
      </div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  )
}
